"use client";

import { getUserTimezone } from "@rmh/utils";
import {
  useState,
  useCallback,
  createContext,
  useContext,
  useMemo,
} from "react";
import type { DateLocalizer, View } from "react-big-calendar";
import { luxonLocalizer } from "react-big-calendar";
import { DateTime, Settings } from "luxon";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchGraphQLClient } from "@rmh/data-ops/src/amplify/fetchGraphQLClient";
import type { CanvasAppointmentEvent } from "@rmh/defs";
import type { ClientCanvasAppointmentEvent } from "@rmh/defs";
import type { GeneratedQuery } from "@aws-amplify/api-graphql";
import type { GetProviderCalendarFilter } from "@rmh/defs";
import { getWeeklySearchRange } from "./utils/getWeeklySearchRange";

const GET_CANVAS_APPOINTMENT_EVENTS_QUERY_KEY = "getCanvasAppointmentEvents";
const DEFAULT_QUERY_KEY = GET_CANVAS_APPOINTMENT_EVENTS_QUERY_KEY;

/**
 * Custom hook for the calendar control, it is recommended to use
 * useMemo and useCallback to memoize the values for better performance
 */
export const useCalendarProps = () => {
  const [view, setView] = useState<View>("week");
  const [date, setDate] = useState<Date>(new Date());
  const [timezone, setTimezone] = useState<string>(getUserTimezone());
  const localizer = useMemo(() => {
    Settings.defaultZone = timezone;
    return luxonLocalizer(DateTime);
  }, [timezone]);

  const onView = useCallback((v: View) => {
    setView(v);
  }, []);

  const onNavigate = useCallback((v: Date) => {
    setDate(v);
  }, []);

  return {
    view,
    date,
    onView,
    onNavigate,
    timezone,
    setTimezone,
    localizer,
  };
};

export interface CalendarContextType {
  view: View;
  date: Date;
  onView: (v: View) => void;
  onNavigate: (v: Date) => void;
  timezone: string;
  setTimezone: (v: string) => void;
  localizer: DateLocalizer;
  events: ClientCanvasAppointmentEvent[];
  isCalendarLoading: boolean;
  invalidateCalendarByDate: (date?: Date) => Promise<void>;
}

const CalendarContext = createContext<CalendarContextType>({
  view: "week",
  date: new Date(),
  onView: () => ({}),
  onNavigate: () => ({}),
  timezone: getUserTimezone(),
  setTimezone: () => ({}),
  localizer: luxonLocalizer(DateTime),
  events: [],
  isCalendarLoading: false,
  invalidateCalendarByDate: async () => {},
});

interface CalendarProviderProps {
  children: React.ReactNode;
  providerIds?: string[];
  query: GeneratedQuery<
    { filter: GetProviderCalendarFilter },
    { getCanvasAppointmentEvents: CanvasAppointmentEvent[] }
  >;
  queryKey?: string;
}

export const CalendarProvider = ({
  children,
  providerIds,
  query,
  queryKey: queryKeyOverride = DEFAULT_QUERY_KEY,
}: CalendarProviderProps) => {
  const { view, date, onView, onNavigate, timezone, setTimezone, localizer } =
    useCalendarProps();

  const queryClient = useQueryClient();
  const [startOfWeek, endOfWeek] = getWeeklySearchRange(date);

  const filter: GetProviderCalendarFilter = useMemo(() => {
    const base: GetProviderCalendarFilter = {
      dateTimeRange: {
        from: startOfWeek.toISO() as string,
        to: endOfWeek.toISO() as string,
      },
    };
    if (providerIds) {
      base.providerIds = providerIds;
    }
    return base;
  }, [startOfWeek, endOfWeek, providerIds]);

  const {
    data: getCanvasAppointmentEventsResponse,
    isPending: isCanvasAppointmentEventsPending,
  } = useQuery({
    queryKey: [queryKeyOverride, startOfWeek.toISO(), ...(providerIds ?? [])],
    queryFn: () =>
      fetchGraphQLClient(query, {
        filter,
      }),
  });

  const invalidateCalendarByDate = useCallback(
    async (invalidationDate?: Date) => {
      try {
        const keys: string[] = [queryKeyOverride];

        if (invalidationDate) {
          const [startOfWeek] = getWeeklySearchRange(invalidationDate);
          keys.push(startOfWeek.toISO() as string);
        }

        await queryClient.invalidateQueries({
          queryKey: keys,
        });
      } catch (error) {
        console.error("Error invalidating calendar", error);
      }
    },
    [queryClient, queryKeyOverride]
  );

  // Flat list of events for the current displaying week, with ISO timestamps
  // converted to JS Date objects for react-big-calendar.
  const events = useMemo<ClientCanvasAppointmentEvent[]>(() => {
    return (
      getCanvasAppointmentEventsResponse?.data.getCanvasAppointmentEvents.map(
        (event) => ({
          ...event,
          // Key the event on its interval — react-big-calendar uses the id for
          // selection and keyed rendering.
          id: `${event.start}/${event.end}`,
          start: DateTime.fromISO(event.start).toJSDate(),
          end: DateTime.fromISO(event.end).toJSDate(),
        })
      ) ?? []
    );
  }, [getCanvasAppointmentEventsResponse]);

  return (
    <CalendarContext.Provider
      value={{
        view,
        date,
        onView,
        onNavigate,
        timezone,
        setTimezone,
        localizer,
        events,
        isCalendarLoading: isCanvasAppointmentEventsPending,
        invalidateCalendarByDate,
      }}
    >
      {children}
    </CalendarContext.Provider>
  );
};

export const useCalendar = () => useContext(CalendarContext);
